<script setup>
// Two-column content section: a left "section label" (Figma "Headline Level 2")
// and a right main column holding a nested block sequence. Mirrors the
// .what-we-do / .academy two-column idiom in Home.vue.
import ContentBlocks from './ContentBlocks.vue'

defineProps({
  block: { type: Object, required: true },   // { label, content: [...blocks] }
})
</script>

<template>
  <section class="content-section">
    <div class="inner">
      <div class="label">
        <h2 v-if="block.label">{{ block.label }}</h2>
      </div>
      <div class="main">
        <ContentBlocks :blocks="block.content || []" />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.content-section {
  // Width capping is owned by the page-level .blocks container, so the section
  // only lays out its two columns within that column.
  .inner {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-6;

    // jk-37: was from-md (768px) with a *fixed* 433px label column — at
    // 768-1023px ("the md to lg range") that left the main content column
    // uncomfortably narrow (container width minus gutters minus a flat
    // 433px minus the gap, often well under half the page). Single column
    // until from-lg (1024px), then the same proportional ratio
    // SplitHeaderBlock/OurApproach's/OurWorkDetail's own body-grid already
    // use — the label column scales with the viewport instead of eating a
    // fixed amount of it.
    @include from-lg {
      grid-template-columns: minmax(0, 2fr) minmax(0, 5fr);
      gap: $space-8;
    }
  }

  .label h2 {
    margin: 0;
    font-size: $font-size-h2;
    font-weight: 500;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
    color: var(--color-ink);
  }

  // Right column — stacks its nested blocks with generous vertical rhythm.
  .main {
    display: flex;
    flex-direction: column;
    gap: $space-8;
  }
}
</style>
